'use client';

import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Loader2 } from 'lucide-react';
import { AUTH_ENDPOINTS, AUTH_ERROR_CODES } from '@impact/auth';
import { useTenant } from '@/hooks/use-tenant';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button/button';
import { Input } from '@/components/ui/input/input';
import { PasswordInput } from '@/components/ui/input/password-input';
import { Label } from '@/components/ui/label/label';
import { createLoginSchema, type LoginFormData } from '@/tenants/_shared/schemas/registration';

const HERO_IMAGE_URL = '/images/dgd/DGD_HeroImage2.jpg';

export function LoginPage() {
  const t = useTranslations('auth');
  const tNav = useTranslations('nav');
  const tenantId = useTenant();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if already authenticated — redirect without flashing the form
  const authQuery = trpc.authMe.me.useQuery(undefined, {
    enabled: !!tenantId,
    retry: false,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (authQuery.data?.redirectUrl) {
      window.location.href = authQuery.data.redirectUrl;
    }
  }, [authQuery.data]);

  const loginSchema = useMemo(() => createLoginSchema(t), [t]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(AUTH_ENDPOINTS.tenant.signInEmail, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(tenantId && { 'X-Tenant-ID': tenantId }),
        },
        body: JSON.stringify(data),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = ((await response.json().catch(() => null)) ?? {}) as {
          code?: string;
          message?: string;
        };
        throw new Error(
          errorData.code === AUTH_ERROR_CODES.legacyPasswordResetRequired
            ? t('legacyPasswordResetRequiredMessage')
            : errorData.message || t('loginFailed'),
        );
      }

      // Server-side redirect reads the session cookie directly — no race condition
      window.location.href = '/auth/redirect';
    } catch (err) {
      setError(err instanceof Error ? err.message : t('loginFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show hero background while checking auth — no form flash
  if (authQuery.isLoading) {
    return (
      <div className="relative flex min-h-[85vh] items-center justify-center">
        <div className="absolute inset-0 z-0">
          <Image src={HERO_IMAGE_URL} alt="" fill className="object-cover" priority />
          <div className="absolute inset-0 bg-black/60" />
        </div>
      </div>
    );
  }

  const inputClasses =
    'h-11 rounded-lg bg-stone-900 border-stone-700 text-white placeholder:text-stone-500 focus-visible:ring-2 focus-visible:ring-dgd-gold/40 focus-visible:border-dgd-gold transition-colors';

  return (
    <div
      className="notranslate relative flex min-h-[85vh] items-center justify-center px-4 py-12"
      translate="no"
    >
      {/* Hero Background */}
      <div className="absolute inset-0 z-0">
        <Image src={HERO_IMAGE_URL} alt="" fill className="object-cover" priority />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/70 to-black/80" />
      </div>

      {/* Login Card */}
      <div
        className="relative z-10 w-full max-w-[420px]"
        style={{ viewTransitionName: 'dgd-login-card' }}
      >
        <div className="rounded-2xl border border-stone-700/50 bg-stone-950/90 p-6 sm:p-8 shadow-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold tracking-tight text-white">{t('welcomeBack')}</h1>
            <p className="mt-2 text-sm text-stone-400">{t('loginSubtitle')}</p>
          </div>

          <form
            onSubmit={(e) => {
              void handleSubmit(onSubmit)(e);
            }}
            className="space-y-5"
            noValidate
          >
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="login-email" className="text-sm font-medium text-stone-300">
                {t('emailAddress')}
              </Label>
              <Input
                id="login-email"
                type="email"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="username"
                spellCheck={false}
                placeholder={t('emailPlaceholder')}
                {...register('email')}
                disabled={isSubmitting}
                aria-invalid={errors.email ? 'true' : undefined}
                aria-describedby={errors.email ? 'email-error' : undefined}
                className={inputClasses}
              />
              {errors.email && (
                <p id="email-error" role="alert" className="text-sm text-red-400">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="login-password" className="text-sm font-medium text-stone-300">
                  {t('password')}
                </Label>
                <Link
                  href="/forgot-password"
                  prefetch={false}
                  className="text-xs font-medium text-stone-400 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dgd-gold/40 rounded-sm"
                >
                  {t('forgotPasswordLink')}
                </Link>
              </div>
              <PasswordInput
                id="login-password"
                autoComplete="current-password"
                placeholder={t('passwordPlaceholder')}
                {...register('password')}
                disabled={isSubmitting}
                aria-invalid={errors.password ? 'true' : undefined}
                aria-describedby={errors.password ? 'password-error' : undefined}
                className={inputClasses}
              />
              {errors.password && (
                <p id="password-error" role="alert" className="text-sm text-red-400">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Error */}
            {error && (
              <div
                role="alert"
                className="rounded-lg bg-red-950/50 border border-red-900/50 px-4 py-3 text-sm text-red-400"
              >
                {error}
              </div>
            )}

            {/* Submit */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-11 rounded-lg bg-dgd-gold text-dgd-charcoal text-sm font-semibold hover:bg-dgd-gold-hover focus-visible:ring-2 focus-visible:ring-dgd-gold/40 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-950 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('loggingIn')}
                </>
              ) : (
                t('loginButton')
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="mt-6 mb-5 border-t border-stone-800" />

          {/* Register Link */}
          <p className="text-center text-sm text-stone-400">
            {t('noAccount')}{' '}
            <Link
              href="/register"
              prefetch={false}
              className="font-medium text-dgd-gold hover:text-dgd-gold-light transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dgd-gold/40 rounded-sm"
            >
              {tNav('register')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
